import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ColDef } from 'ag-grid-community';
import { Ams } from 'src/app/amsconfig';
import {
  IUserColumnChoice,
  IMachineTools,
  IMachineSetup,
  IMachineSetupCompare,
  IMachineConfigSnapshot,
  IMachineSetupsConfigs,
  IMachineToolingConfigDto,
  IMachineToolsConfigs,
  IToolingConfigChange,
  IMachinePrintConfiguration,
  IPunchPattern,
  IMachineAxes,
  IMachineSetupHistoryItem,
  MachinePattern,
  IPathfinderMachine,
  IUser,
  PatternDef,
  IMachineUserFields,
  IMachineUserFieldPatch,
  IUserFieldMacro,
  IFormatOption,
  ISegmentAliasTable,
  ISegmentGeometryTable,
  ISegmentWorksheetArg,
  ISegmentWorksheetResult,
  IShapeGeometryTable,
  IToolingCodePatternTable,
  AvailableMacro,
} from 'src/app/core/dto';
import { IShapeTable } from '../components/xl200-shape-table-editor/types';
import { SetUpUrl } from '../../machines/xl200/url-constant';
import { IWorkgroup } from '../../settings/loss-code/types';
import { Observable, expand, EMPTY, concat, of } from 'rxjs';
import { map, reduce, mergeMap } from 'rxjs/operators';

/**
 * Plan-view orientation of a machine on the floor, operator-relative. Shared by the
 * visualizations that draw the machine or its parts as the operator sees them
 * (pattern flat view, machine layout).
 */
export interface IMachineOrientation {
  /** Part travels left→right as seen from the operator side (exit at the right). */
  flowRight: boolean;
  /** The machine + side is away from the operator. */
  plusSideFar: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class Xl200Service {
  private http = inject(HttpClient);

  private get baseUrl(): string {
    return Ams?.Config?.BASE_URL || '';
  }

  getMachineSettingColumns() {
    return this.http.get<IUserColumnChoice[]>(
      `${this.baseUrl}/_api/user/settings/machineColumns`
    );
  }

  updateMachineSettingColumns(data: ColDef[]) {
    return this.http.post<IUserColumnChoice[]>(
      `${this.baseUrl}/_api/user/settings/machineColumns`,
      {
        data,
      }
    );
  }

  getMachineSetup(machineId: number): Observable<IMachineSetup[]> {
    const pageSize = 100;

    const fetchPage = (skip: number): Observable<IMachineSetup[]> => {
      return this.http.get<IMachineSetup[]>(
        `${Ams.Config.BASE_URL}/_api/machine/${machineId}/${SetUpUrl}?skip=${skip}&take=${pageSize}`
      );
    };

    // Start with skip=0
    return fetchPage(0).pipe(
      expand((pageData, index) => {
        // If we got a full page, there might be more data
        if (pageData.length === pageSize) {
          // Calculate the skip for the next page based on all pages fetched so far
          const nextSkip = (index + 1) * pageSize;
          return fetchPage(nextSkip);
        }
        // If we got less than a full page, we're done
        return EMPTY;
      }),
      // Accumulate all pages into a single array
      reduce((acc: IMachineSetup[], pageData: IMachineSetup[]) => {
        return [...acc, ...pageData];
      }, [])
    );
  }

  getMachineSetupSnapShot(machineId: number, snapshotId: string) {
    return this.http.get<IMachineSetup>(
      Ams.Config.BASE_URL + `/_api/machine/${machineId}/${SetUpUrl}/${snapshotId}`
    );
  }

  setSetupLibrary(
    machineId: number,
    setupId: string,
    body: { mode: 'Save' | 'Rename' | 'Remove'; libraryName: string }
  ) {
    return this.http.put(
      `${this.baseUrl}/_api/machine/${machineId}/${SetUpUrl}/${setupId}/library`,
      body
    );
  }

  getCurrentMachineSetup(machineId: number) {
    return this.http.get<IMachineSetup>(
      Ams.Config.BASE_URL + `/_api/machine/${machineId}/setups-new/.current`
    );
  }

  getMachineSetupCompare(machineId: number, param: string) {
    return this.http.get<IMachineSetupCompare>(
      Ams.Config.BASE_URL + `/_api/machine/${machineId}/${SetUpUrl}/.compare?setupIds=${param}`
    );
  }

  getMachineTools(machineId: number) {
    return this.http.get<IMachineTools[]>(`${this.baseUrl}/_api/machine/${machineId}/tools`);
  }

  getShapeTable(machineId: number) {
    return this.http.get<IShapeTable>(
      `${this.baseUrl}/_api/machine/${machineId}/shapetable`
    );
  }

  putShapeTable(machineId: number, shapeTable: IShapeTable) {
    return this.http.put<IShapeTable>(
      `${this.baseUrl}/_api/machine/${machineId}/shapetable`,
      shapeTable
    );
  }

  createShapeTable(machineId: number) {
    return this.http.get<IShapeTable>(
      `${this.baseUrl}/_api/machine/${machineId}/shapetablestarter`
    );
  }

  getMachineOrientation(machineId: number) {
    return this.http.get<IMachineOrientation>(
      `${this.baseUrl}/_api/machine/${machineId}/orientation`
    );
  }

  saveMachineOrientation(machineId: number, orientation: IMachineOrientation) {
    return this.http.put<IMachineOrientation>(
      `${this.baseUrl}/_api/machine/${machineId}/orientation`,
      orientation
    );
  }

  getSegmentAliasTable(machineId: number) {
    return this.http.get<ISegmentAliasTable>(
      `${this.baseUrl}/_api/machine/${machineId}/segmentaliastable`
    );
  }

  putSegmentAliasTable(machineId: number, table: ISegmentAliasTable) {
    return this.http.put<ISegmentAliasTable>(
      `${this.baseUrl}/_api/machine/${machineId}/segmentaliastable`,
      table
    );
  }

  getSegmentGeometryTable(machineId: number) {
    return this.http.get<ISegmentGeometryTable>(
      `${this.baseUrl}/_api/machine/${machineId}/segmentgeometrytable`
    );
  }

  putSegmentGeometryTable(machineId: number, table: ISegmentGeometryTable) {
    return this.http.put<ISegmentGeometryTable>(
      `${this.baseUrl}/_api/machine/${machineId}/segmentgeometrytable`,
      table
    );
  }

  getToolingCodePatternTable(machineId: number) {
    return this.http.get<IToolingCodePatternTable>(
      `${this.baseUrl}/_api/machine/${machineId}/toolingcodepatterntable`
    );
  }

  putToolingCodePatternTable(machineId: number, table: IToolingCodePatternTable) {
    return this.http.put<IToolingCodePatternTable>(
      `${this.baseUrl}/_api/machine/${machineId}/toolingcodepatterntable`,
      table
    );
  }

  computeSegmentWorksheet(machineId: number, arg: ISegmentWorksheetArg) {
    return this.http.post<ISegmentWorksheetResult>(
      `${this.baseUrl}/_api/machine/${machineId}/segmentworksheet`,
      arg
    );
  }

  getMachinePrintConfiguration(machineId: number) {
    return this.http.get<IMachinePrintConfiguration>(
      `${this.baseUrl}/_api/machine/${machineId}/partprinting`
    );
  }

  getPunchPatterns() {
    return this.http.get<IPunchPattern[]>(
      `${this.baseUrl}/_api/punchpatterns?skip=0&take=1000&permanentOnly=true&notMacroOnly=true`
    );
  }

  getPunchPatternById(patternId: string) {
    return this.http.get<PatternDef>(`${this.baseUrl}/_api/punchpatterns/${patternId}`);
  }

  patchMachineDetail(
    machineNumber: number,
    data: {
      [x: string]: any;
      machineNumber: number;
    }
  ) {
    return this.http.patch(`${this.baseUrl}/_api/machine/${machineNumber}`, data);
  }

  getMachineAxes(machineId: number) {
    return this.http.get<IMachineAxes[]>(`${this.baseUrl}/_api/machine/${machineId}/axes`);
  }

  getMachineSetupHistories(machineId: string, paramId: string, setupId: string) {
    return this.http.get<IMachineSetupHistoryItem[]>(
      `${this.baseUrl}/_api/machine/${machineId}/${SetUpUrl}/.history/${paramId}?setupId=${setupId}`
    );
  }

  savePrintConfig(machineNumber: number, partPrintConfig: IMachinePrintConfiguration) {
    return this.http.put(
      `${this.baseUrl}/_api/machine/${machineNumber}/partprinting`,
      partPrintConfig
    );
  }

  getAvailableShapes() {
    return this.http.get<string[]>(`${this.baseUrl}/_api/availableshapes`);
  }

  // Every macro with its kind (only shape macros can go on a machine's ignore list).
  getAllMacros() {
    return this.http.get<AvailableMacro[]>(`${this.baseUrl}/_api/availableMacros`);
  }

  // The shape geometry library: drawn-hole geometry per machine shape code (global singleton).
  getShapeGeometryTable() {
    return this.http.get<IShapeGeometryTable>(`${this.baseUrl}/_api/shapegeometrytable`);
  }

  saveShapeGeometryTable(data: IShapeGeometryTable) {
    return this.http.put(`${this.baseUrl}/_api/shapegeometrytable`, data);
  }

  getMachinePattern(machineId: number) {
    return this.http.get<MachinePattern[]>(
      Ams.Config.BASE_URL + `/_api/punchpatterns/machine/${machineId}`
    );
  }

  deleteMachinePattern(machineId: number, patternNumber: number) {
    return this.http.patch(
      `${this.baseUrl}/_api/machinepattern/${machineId}/${patternNumber}`,
      { Action: 'D', Completed: false }
    );
  }

  getPathFinderMachines(id: string) {
    return this.http.post<IPathfinderMachine[]>(
      `${this.baseUrl}/_api/folders/${id}/accept`,
      {}
    );
  }

  updateFolderName(id: string, name: string) {
    return this.http.patch<IPathfinderMachine[]>(`${this.baseUrl}/_api/folders/${id}`, {
      name,
    });
  }

  updateUserRole(machineId: string, role: string, userName: string) {
    return this.http.post<IUser>(
      `${this.baseUrl}/_api/folders/${machineId}/pfpcrole?role=${role}&user=${userName}`,
      {}
    );
  }

  saveUserPin(userName: string, pin: string) {
    return this.http.post<IUser>(
      `${this.baseUrl}/_api/users/${userName}/setpin?pin=${pin}`,
      {}
    );
  }

  getSetupConfigs(machineId: number): Observable<IMachineSetupsConfigs[]> {
    return this.http.get<IMachineSetupsConfigs[]>(
      `${this.baseUrl}/_api/machine/${machineId}/setups-new/configs`
    );
  }

  getToolConfigs(machineId: number): Observable<IMachineToolsConfigs[]> {
    return this.http.get<IMachineToolsConfigs[]>(
      `${this.baseUrl}/_api/machine/${machineId}/toolconfigs`
    );
  }

  /// The machine's setup config history, newest first, without the configs themselves.
  getSetupConfigSnapshots(machineId: number): Observable<IMachineConfigSnapshot[]> {
    return this.http.get<IMachineConfigSnapshot[]>(
      `${this.baseUrl}/_api/machine/${machineId}/setups-new/configs/snapshots`
    );
  }

  getSetupConfigSnapshot(
    machineId: number,
    snapshotId: string
  ): Observable<IMachineSetupsConfigs> {
    return this.http.get<IMachineSetupsConfigs>(
      `${this.baseUrl}/_api/machine/${machineId}/setups-new/configs/${snapshotId}`
    );
  }

  /// The machine's tool config history, newest first, without the tool configs themselves.
  getToolConfigSnapshots(machineId: number): Observable<IMachineConfigSnapshot[]> {
    return this.http.get<IMachineConfigSnapshot[]>(
      `${this.baseUrl}/_api/machine/${machineId}/toolconfigs/snapshots`
    );
  }

  getToolConfigSnapshot(
    machineId: number,
    snapshotId: string
  ): Observable<IMachineToolsConfigs> {
    return this.http.get<IMachineToolsConfigs>(
      `${this.baseUrl}/_api/machine/${machineId}/toolconfigs/${snapshotId}`
    );
  }

  getMachineToolingConfigs(machineId: number): Observable<IMachineToolingConfigDto[]> {
    return this.http.get<IMachineToolingConfigDto[]>(
      `${this.baseUrl}/_api/machine/${machineId}/tooling-configs`
    );
  }

  saveMachineToolingConfigs(
    machineId: number,
    changes: IToolingConfigChange[]
  ): Observable<IMachineToolingConfigDto[]> {
    return this.http.put<IMachineToolingConfigDto[]>(
      `${this.baseUrl}/_api/machine/${machineId}/tooling-configs`,
      changes
    );
  }

  getMachineUserFields(machineId: number) {
    return this.http.get<IMachineUserFields>(
      `${this.baseUrl}/_api/machine/${machineId}/userfields`
    );
  }

  patchMachineUserFields(machineId: number, patches: IMachineUserFieldPatch[]) {
    return this.http.patch<IMachineUserFields>(
      `${this.baseUrl}/_api/machine/${machineId}/userfields`,
      patches
    );
  }

  getUserFieldMacros(fieldGroup: 'Order' | 'Item') {
    return this.http.get<IUserFieldMacro[]>(
      `${this.baseUrl}/_api/userfield-macros?fieldGroup=${fieldGroup}`
    );
  }

  getFormatOptions(category: string) {
    return this.http.get<IFormatOption[]>(
      `${this.baseUrl}/_api/userfield-format-options?category=${category}`
    );
  }
}
